<template>
  <SettingsPaneV2 header="Security">
    <template #subheader>
      Manage your security measures and make sure your workspace is secure.
    </template>
    <template #body>
      <div class="body__section banner">
        <div class="text-container">
          <p class="control__toggle-label">About security on the V7 platform</p>
          <p class="control__toggle-sub-label">
            We take great care of the security of your personal and business data.
          </p>

          <DarwinButton
            class="text-container--button"
            size="md"
            kind="secondary"
            flair="rounded"
            to="https://www.v7labs.com/terms/security"
            target="_blank"
          >
            Learn more about SSO
          </DarwinButton>
        </div>
        <div class="image-container">
          <div class="security-image" />
          <div class="security-gradient-image" />
        </div>
      </div>
      <template v-if="canUpdateTeam">
        <Divider class="divider" />
        <div
          class="body__section"
          data-test="export-token-section"
        >
          <div class="control__toggle">
            <div class="control__toggle-description">
              <p class="control__toggle-label">Export token TTL</p>
              <p class="control__toggle-sub-label">Restrict access to export for a given time</p>
            </div>
            <SelectField
              :value="exportTokenTtl"
              :options="exportTokenOptions"
              :show-icon-prepend="false"
              class="export-token-select"
              size="md"
              @change="onSelect"
            >
              <template #option="{ option, selected }">
                <ListItemWithState
                  :selected="selected"
                  size="sm"
                >
                  <p>{{ option.text }}</p>
                </ListItemWithState>
              </template>
            </SelectField>
          </div>
        </div>
      </template>
      <template v-if="canUpdateTeam">
        <Divider class="divider" />
        <div
          class="body__section"
          data-test="sso-section"
        >
          <ToolTip :content="tooltip">
            <div class="control__toggle">
              <Toggle
                :model-value="ssoAuthEnforced"
                :disabled="!isSSOAndSAMLGated && !hasSSOConfig"
                kind="primary"
                size="small"
                @update:model-value="onSelectSSO"
              />
              <div class="control__toggle-description">
                <p class="control__toggle-label">Enforce single sign-on</p>
                <p class="control__toggle-sub-label">Force users to log in exclusively with SSO</p>
              </div>
              <ToolTip
                v-if="isSSOAndSAMLGated"
                content="Not available on current plan"
              >
                <Icon name="duotone-star" />
              </ToolTip>
            </div>
          </ToolTip>
          <div class="sso__setting">
            <RoleDropdown
              :value="defaultRole"
              label="Default role"
              :disabled="isSSOAndSAMLGated || !hasSSOConfig"
              :options="roleOptions"
              @input="defaultRole = $event"
            />

            <InputField
              id="businessDomain"
              v-model="businessDomain"
              type="text"
              :disabled="isSSOAndSAMLGated || !hasSSOConfig"
              label="Business domain"
              placeholder="i.e. @v7labs.com"
              :error="error.domain"
            />

            <DarwinButton
              class="sso__setting__button"
              size="md"
              kind="secondary"
              flair="rounded"
              @click="
                () =>
                  isSSOAndSAMLGated ? (upgradeDialogShown = true) : (ssoConfigDialogOpened = true)
              "
            >
              {{
                teamStore.currentTeam?.managed_status === 'client' ? 'View SSO' : 'Configure SSO'
              }}
            </DarwinButton>
          </div>
          <SsoConfigModal
            v-model:open="ssoConfigDialogOpened"
            v-model:sso-config="ssoConfig"
            v-model:has-existing-config="hasSSOConfig"
            :team-id="assert(teamStore.currentTeam).id"
            :readonly="assert(teamStore.currentTeam).managed_status === 'client'"
          />
        </div>
      </template>
      <div
        v-if="canUpdateTeam"
        class="body__section"
        data-test="2fa-section"
      >
        <div class="control__toggle">
          <Toggle
            :model-value="twoFactorAuthEnforced"
            kind="primary"
            size="small"
            @update:model-value="onSelect2fa"
          />

          <div class="control__toggle-description">
            <p class="control__toggle-label">Enforce two factor authentication</p>
            <p class="control__toggle-sub-label">
              Enforce users in your workspace to use 2FA when connecting to V7.
            </p>
          </div>
          <ToolTip
            v-if="isTFAEnforcementGated"
            content="Not available on current plan"
          >
            <Icon name="duotone-star" />
          </ToolTip>
        </div>
      </div>
      <DarwinDialog
        :open="upgradeDialogShown"
        fullscreen
        max-height="900"
        @keydown:esc="upgradeDialogShown = false"
      >
        <UpgradeDialog
          v-if="upgradeDialogShown"
          @close="upgradeDialogShown = false"
        >
          <template #title> Upgrade your plan to add SSO and 2FA </template>
        </UpgradeDialog>
      </DarwinDialog>
    </template>

    <template #footer>
      <DarwinButton
        size="md"
        kind="primary"
        flair="rounded"
        :disabled="loading"
        @click="saveAll"
      >
        Save
      </DarwinButton>
    </template>
  </SettingsPaneV2>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import InputField from '@/components/Common/InputField/V2/InputField.vue'
import RoleDropdown from '@/modules/Teams/RoleDropdown.vue'

import { useAuth } from '@/modules/Auth/useAuth'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useToast } from '@/uiKit/Toast/useToast'
import { validateBusinessDomain } from '@/core/utils/validation'
import { loadSSOConfig } from '@/backend/darwin/loadSSOConfig'

import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import DarwinDialog from '@/uiKit/Dialog/DarwinDialog.vue'
import Divider from '@/uiKit/Divider/Divider.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import ListItemWithState from '@/uiKit/List/ListItemWithState.vue'
import SelectField from '@/uiKit/SelectField/SelectField.vue'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import type { OptionType } from '@/uiKit/types'
import { assert } from '@/core/utils/assert'

import {
  annotationCreditsBilledNextPeriod,
  resolvePlanForCredit,
} from '@/modules/Billing/cbbProductUtils'
import UpgradeDialog from '@/modules/Billing/UpgradeDialog.vue'
import { useResourceTFAEnforcement } from '@/modules/Billing/useResourceTFAEnforcement'
import { useResourceSSOAndSAML } from '@/modules/Billing/useResourceSSOAndSAML'
import { useBillingInfoLoader } from '@/modules/Billing/useBillingInfoLoader'
import { useBillingStore } from '@/modules/Billing/useBillingStore'

import SettingsPaneV2 from './SettingsPaneV2.vue'
import SsoConfigModal from './SsoConfigModal.vue'
import { getMembershipRoleOptions } from '@/modules/Teams/roleData'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import { createUpdateTeamError, type UpdateTeamPayload } from '@/modules/Teams/types'

const { isAuthorized } = useAuth()
const billingInfoLoader = useBillingInfoLoader()
const billingStore = useBillingStore()
const { isSSOAndSAMLGated } = useResourceSSOAndSAML()
const { isTFAEnforcementGated } = useResourceTFAEnforcement()
const teamStore = useTeamStore()
const toast = useToast()

const error = ref<{ domain?: string }>({})
const upgradeDialogShown = ref(false)
const loading = ref(false)
const twoFactorAuthEnforced = ref(false)
const ssoAuthEnforced = ref(false)
const exportTokenTtl = ref('0')
const businessDomain = ref('')
const defaultRole = ref('member')
const ssoConfigDialogOpened = ref(false)
const hasSSOConfig = ref<boolean>(false)
const ssoConfig = ref<string>('')

const exportTokenOptions = [
  { id: '0', text: 'Never expire' },
  { id: '720', text: '30 days' },
]

const roleOptions = computed(() => getMembershipRoleOptions(isAuthorized))

const canUpdateTeam = computed(() => isAuthorized('update_team'))

const tooltip = computed(() =>
  !hasSSOConfig.value && !isSSOAndSAMLGated.value
    ? 'You need to set up the SSO config first.'
    : undefined,
)

const onSelect = (option: OptionType): void => {
  const { id } = option
  exportTokenTtl.value = id
}

const onSelectSSO = (): void => {
  if (isSSOAndSAMLGated.value) {
    upgradeDialogShown.value = true
    return
  }
  ssoAuthEnforced.value = !ssoAuthEnforced.value
  businessDomain.value = teamStore.currentTeam?.email_domain || ''
}

const onSelect2fa = (): void => {
  if (isTFAEnforcementGated.value) {
    upgradeDialogShown.value = true
    return
  }
  twoFactorAuthEnforced.value = !twoFactorAuthEnforced.value
}

const validateForm = (): boolean => {
  error.value = {}

  if (businessDomain.value?.length) {
    const domainIssues = validateBusinessDomain(businessDomain.value)
    if (domainIssues.length > 0) {
      error.value.domain = domainIssues[0]
    }
  }

  return Object.keys(error.value).length === 0
}

const saveAll = async (): Promise<void> => {
  if (!validateForm()) {
    return
  }

  if (!teamStore.currentTeam) {
    return
  }

  loading.value = true

  const teamId = teamStore.currentTeam.id
  const params: UpdateTeamPayload = {
    export_token_ttl: exportTokenTtl.value === '0' ? null : Number(exportTokenTtl.value),
    two_factor_auth_enforced: twoFactorAuthEnforced.value,
    sso_enforced: ssoAuthEnforced.value,
  }
  const billedNextPeriod = billingStore.billingInfo
    ? annotationCreditsBilledNextPeriod(billingStore.billingInfo)
    : 0

  if (ssoAuthEnforced.value || resolvePlanForCredit(billedNextPeriod)) {
    params.email_domain = businessDomain.value
    params.default_role = defaultRole.value
  }

  const response = await useTeamStore().updateTeam(teamId, params)

  loading.value = false

  if (!response.ok) {
    const updateError = createUpdateTeamError(response.error)
    error.value = {}
    error.value.domain = updateError.emailDomain

    if (updateError.ssoAuthEnforced) {
      toast.warning({
        meta: { title: updateError.ssoAuthEnforced },
      })
    }

    return
  }

  toast.success({ meta: { title: 'Workspace successfully updated' } })
}

const updateData = async (): Promise<void> => {
  if (!teamStore.currentTeam) {
    return
  }

  twoFactorAuthEnforced.value = teamStore.currentTeam.two_factor_auth_enforced
  ssoAuthEnforced.value = teamStore.currentTeam.sso_enforced
  exportTokenTtl.value =
    teamStore.currentTeam.export_token_ttl === null
      ? '0'
      : teamStore.currentTeam.export_token_ttl.toString()
  defaultRole.value = teamStore.currentTeam.default_role || 'member'
  businessDomain.value = teamStore.currentTeam.email_domain || ''

  const teamId =
    teamStore.currentTeam.managed_status === 'client'
      ? (teamStore.currentTeam.partner_id as number)
      : teamStore.currentTeam.id

  const result = await loadSSOConfig(teamId)
  if (result.ok) {
    hasSSOConfig.value = true
    ssoConfig.value = result.data.idp_metadata
  }

  billingInfoLoader.load(teamStore.currentTeam.id)
}

watch(
  () => teamStore.currentTeam,
  () => updateData(),
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.body__section {
  margin-bottom: 16px;
}

.divider {
  margin: 32px 0;
}

.control__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.control__toggle-description {
  flex: 1;
}
.control__toggle-description:not(:first-child) {
  & .control__toggle-label,
  & .control__toggle-sub-label {
    margin-left: 16px;
  }
}

.control__toggle-description:not(:first-child) {
  & .control__toggle-label,
  & .control__toggle-sub-label {
    margin-left: 16px;
  }
}

.control__toggle-label,
.control__toggle-sub-label {
  @include typographyRegularBody200;
  color: var(--color-text-default);
}
.control__toggle-sub-label {
  color: var(--color-text-subtlest);
}

.banner {
  @include row;
  align-items: center;
  background-color: var(--color-surface-secondary);
  border-radius: 16px;
}
.text-container {
  padding: 20px 24px;

  & .control__toggle-label {
    @include typographyBoldBody200;
  }
}
.text-container--button {
  margin-top: 25px;
}

.image-container {
  position: relative;
  flex: 1;
}
.security-image {
  background-image: url('./assets/security.svg');
  background-repeat: no-repeat;
  height: 164px;
  width: 298px;
}
.security-gradient-image {
  position: absolute;
  top: 0;
  right: 0;
  background-image: url('./assets/gradient.svg');
  height: 164px;
  width: 296px;
  border-radius: 16px;
}

.sso__setting {
  @include row;
  gap: 16px;
  margin-left: 44px;
  margin-top: 16px;

  &__button {
    width: 100%;
    margin-top: 27px;
  }
}
</style>
